import type { Claimant, FamilyMember } from "@/types";

/**
 * Check if a claimant has active stoploss claims.
 * Primary signal: stoploss_claim_count from the list/get handler (batch-enriched).
 * Fallback: checks policy_data JSONB for claim_count and total_submitted to cover
 * both raw JSONB and snapshot data sources.
 */
export const claimantHasStoplossClaims = (c: Claimant): boolean =>
  (c.stoploss_claim_count !== null && c.stoploss_claim_count !== undefined && c.stoploss_claim_count > 0) ||
  (c.policy_data
    ? Object.values(c.policy_data).some(
        (pd) =>
          (pd?.stoploss?.calculated?.claim_count && pd.stoploss.calculated.claim_count > 0) ||
          (pd?.stoploss?.calculated?.total_submitted && pd.stoploss.calculated.total_submitted > 0)
      )
    : false);

/**
 * Count total family members by deduplicating API members and locally-added members.
 * Members present in both lists are counted once.
 */
export const calculateFamilyMemberCount = (members: FamilyMember[], localMembers: FamilyMember[]): number => {
  const apiIds = new Set(members.map((m) => m.id));
  const newLocalCount = localMembers.filter((lm) => !apiIds.has(lm.id)).length;
  return members.length + newLocalCount;
};

/**
 * Check if the family has reached (or exceeded) the maximum member capacity.
 * Returns false when maxMembers is null (no limit configured).
 */
export const isAtFamilyCapacity = (memberCount: number, maxMembers: number | null): boolean => {
  if (maxMembers === null) return false;
  return memberCount >= maxMembers;
};
